/**
 * Inverse-parse branch for the JSON filter operator (`predicateToFilterCondition`'s
 * seam c per the task brief) — turns the SQL `buildJsonConditionSql`
 * (`common/utils/jsonFilterSql.ts`, NOT modified here) emits back into a
 * structured `{ field, condition }` pair so a re-edited JSON filter chip
 * re-opens as the rich JSON path/sub-operator chip instead of dropping to raw
 * text (PARITY-JSON-01 / PARITY-LOOSEFK-01's JSON-path half).
 *
 * MSSQL / MySQL / Oracle / SQLite are handled here (their JSON emit shapes
 * share the "extract-as-text expression + optional declared-on-text AND
 * guard" pattern). PostgreSQL emits a structurally different shape (a field
 * ALWAYS coerced to `::jsonb`, and `{a,b}`-array-literal paths instead of
 * `$.a.b` JSONPath strings) — see `predicateJsonBranchPg.ts`.
 *
 * Scope (conservative — see COMMON.md's round-trip guardrail): covers the
 * non-wildcard, non-recursive-descent scalar sub-operators —
 * `has_property` / `not_has_property`, `path_eq` (also covers
 * `property_lookup`'s byte-identical SQL shape — the caller upgrades to
 * `property_lookup` when a loose-FK match justifies it), `property_enum`
 * (IN-list form only — a scalar enum value is byte-identical to `path_eq`
 * and reconstructs as that), `path_neq`, `path_is_null` / `path_is_not_null`,
 * `path_contains`, `path_gt` / `path_gte` / `path_lt` / `path_lte`, and
 * `is_valid` / `is_not_valid` (declared-on-text form only — the native-column
 * form degenerates to a bare `IS NOT NULL` / `1 = 0`, which either already
 * round-trips via the pre-existing scalar `IS NOT NULL` branch or has no
 * field reference to recover at all).
 *
 * Not covered here (this returns `null`): `is_empty` / `is_not_empty`,
 * wildcard paths (`any_*` / `all_*` aggregates, `has_property` on a `[*]`
 * path), recursive descent (`..`) paths, and MSSQL's non-root `has_property` /
 * `not_has_property` (the `OPENJSON` parent/key-enumeration shape) — the
 * caller reads those back with `parseJsonPredicateByEmit`
 * (`predicateJsonEmitMatch.ts`).
 */

import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import type { FilterCondition } from '../../core/types/workspaceTypes';
import { CONTAINS_ESCAPE_SRC, containsValueOfPattern } from './containsLikeSql';
import { QUALIFIED_IDENT_SRC } from './predicateIdentGrammar';
import {
  NUMERIC_VALUE_SRC,
  decodeTextLiteral,
  escapeRegExp,
  parseQuotedStringList,
  pathLiteralSrc,
  textValueSrc,
  firstJsonPathMatch,
  JSON_NUMERIC_SUB_OPS,
  type JsonParseResult,
} from './predicateJsonHelpers';
import { parsePgJsonPredicate } from './predicateJsonBranchPg';

type NonPgEngine = 'mssql' | 'mysql' | 'oracle' | 'sqlite';

const FIELD_SRC = `(${QUALIFIED_IDENT_SRC})`;

/** The engine's "extract this JSONPath as text" expression, field+path
 *  captured (mirrors `extractTextSql` in `jsonFilterSql.ts`). */
function extractExprSrc(engine: NonPgEngine): string {
  const path = pathLiteralSrc(engine);
  switch (engine) {
    case 'mysql':
      return `JSON_UNQUOTE\\(JSON_EXTRACT\\(${FIELD_SRC}, ${path}\\)\\)`;
    case 'mssql':
    case 'oracle':
      return `JSON_VALUE\\(${FIELD_SRC}, ${path}\\)`;
    case 'sqlite':
      return `CAST\\(json_extract\\(${FIELD_SRC}, ${path}\\) AS TEXT\\)`;
  }
}

/** Wraps an extract-expr SOURCE with the engine's numeric-cast, mirroring
 *  `castNumericSql`. */
function numericCastSrc(engine: NonPgEngine, exprSrc: string): string {
  switch (engine) {
    case 'mysql':
      return `CAST\\(${exprSrc} AS DECIMAL\\(38,10\\)\\)`;
    case 'mssql':
      return `TRY_CAST\\(${exprSrc} AS DECIMAL\\(38,10\\)\\)`;
    case 'oracle':
      return `TO_NUMBER\\(${exprSrc}\\)`;
    case 'sqlite':
      return `CAST\\(${exprSrc} AS REAL\\)`;
  }
}

type BodyMatch = { field: string; path: string; value?: FilterCondition['value'] };
type GuardedMatch = BodyMatch & { declaredOnText: boolean };

/** Strip the engine's declared-on-text `<guard> AND ` prefix, mirroring
 *  `buildJsonConditionSql`'s `wrap()`. Returns the guard's field + the
 *  remainder, or `null` when the prefix isn't present. */
function matchGuardPrefix(engine: NonPgEngine, s: string): { guardField: string; rest: string } | null {
  let re: RegExp;
  switch (engine) {
    case 'mssql':
      re = new RegExp(`^ISJSON\\(${FIELD_SRC}\\) = 1 AND ([\\s\\S]*)$`);
      break;
    case 'mysql':
      re = new RegExp(`^JSON_VALID\\(${FIELD_SRC}\\) = 1 AND ([\\s\\S]*)$`);
      break;
    case 'oracle':
      re = new RegExp(`^${FIELD_SRC} IS JSON AND ([\\s\\S]*)$`);
      break;
    case 'sqlite':
      re = new RegExp(`^json_valid\\(${FIELD_SRC}\\) = 1 AND ([\\s\\S]*)$`);
      break;
  }
  const m = s.match(re);
  return m ? { guardField: m[1], rest: m[2] } : null;
}

/** Try `matchBody` first with NO guard (declaredOnText=false); if that fails,
 *  try stripping the guard prefix and matching the remainder, requiring the
 *  guard's field to match the body's own field (else bail — a mismatched
 *  guard field means this ISN'T our own emit). */
function withOptionalGuard(
  engine: NonPgEngine,
  s: string,
  matchBody: (body: string) => BodyMatch | null,
): GuardedMatch | null {
  const direct = matchBody(s);
  if (direct) return { ...direct, declaredOnText: false };
  const guard = matchGuardPrefix(engine, s);
  if (!guard) return null;
  const body = matchBody(guard.rest);
  if (!body || body.field !== guard.guardField) return null;
  return { ...body, declaredOnText: true };
}

function mkPathEqMatcher(engine: NonPgEngine): (body: string) => BodyMatch | null {
  const re = new RegExp(`^${extractExprSrc(engine)} = ${textValueSrc(engine)}$`);
  return (body) => {
    const m = body.match(re);
    if (!m) return null;
    const path = decodeTextLiteral(m[2], engine);
    const value = decodeTextLiteral(m[3], engine);
    if (path === null || value === null) return null;
    return { field: m[1], path, value };
  };
}

function mkPropertyEnumMatcher(engine: NonPgEngine): (body: string) => BodyMatch | null {
  const re = new RegExp(`^${extractExprSrc(engine)} IN \\((.+)\\)$`);
  return (body) => {
    const m = body.match(re);
    if (!m) return null;
    const values = parseQuotedStringList(m[3], engine);
    const path = decodeTextLiteral(m[2], engine);
    if (!values || path === null) return null;
    return { field: m[1], path, value: values };
  };
}

function mkPathNeqMatcher(engine: NonPgEngine): (body: string) => BodyMatch | null {
  const ext = extractExprSrc(engine);
  const re = new RegExp(`^\\(${ext} IS NULL OR ${ext} <> ${textValueSrc(engine)}\\)$`);
  return (body) => {
    const m = body.match(re);
    if (!m) return null;
    const [, f1, p1, f2, p2, v] = m;
    if (f1 !== f2 || p1 !== p2) return null;
    const path = decodeTextLiteral(p1, engine);
    const value = decodeTextLiteral(v, engine);
    if (path === null || value === null) return null;
    return { field: f1, path, value };
  };
}

function mkPathIsNullMatcher(engine: NonPgEngine, negate: boolean): (body: string) => BodyMatch | null {
  const re = new RegExp(`^${extractExprSrc(engine)} IS ${negate ? 'NOT ' : ''}NULL$`);
  return (body) => {
    const m = body.match(re);
    if (!m) return null;
    const path = decodeTextLiteral(m[2], engine);
    if (path === null) return null;
    return { field: m[1], path };
  };
}

function mkPathContainsMatcher(engine: NonPgEngine): (body: string) => BodyMatch | null {
  const ext = extractExprSrc(engine);
  const re = new RegExp(`^LOWER\\(${ext}\\) LIKE LOWER\\(${textValueSrc(engine)}\\)${CONTAINS_ESCAPE_SRC}$`);
  return (body) => {
    const m = body.match(re);
    if (!m) return null;
    const [, field, pathLit, quoted, escapeClause] = m;
    const path = decodeTextLiteral(pathLit, engine);
    const raw = decodeTextLiteral(quoted, engine);
    if (path === null || raw === null) return null;
    // The pattern `containsLikeSql` wrote (or the backslash form saved before).
    const value = containsValueOfPattern(raw, escapeClause, engine);
    return value === null ? null : { field, path, value };
  };
}

function mkNumericOpMatcher(
  engine: NonPgEngine,
  op: '>' | '>=' | '<' | '<=',
): (body: string) => BodyMatch | null {
  const cast = numericCastSrc(engine, extractExprSrc(engine));
  const re = new RegExp(`^${cast} ${escapeRegExp(op)} ${NUMERIC_VALUE_SRC}$`);
  return (body) => {
    const m = body.match(re);
    if (!m) return null;
    const path = decodeTextLiteral(m[2], engine);
    if (path === null) return null;
    return { field: m[1], path, value: Number(m[3]) };
  };
}

/** `has_property` / `not_has_property` — each engine's shape is distinct
 *  from the extract-expr family, so these are matched directly (not via
 *  `withOptionalGuard`'s `matchBody` signature, though the guard-strip logic
 *  is the same pattern inlined below). SQLite intentionally has NO entry —
 *  its `has_property` SQL is BYTE-IDENTICAL to `path_is_not_null` (both are
 *  `CAST(json_extract(...) AS TEXT) IS NOT NULL`), so that branch already
 *  round-trips this shape losslessly; adding a separate sqlite matcher here
 *  would be unreachable dead code. */
function matchHasPropertyBody(engine: NonPgEngine, body: string, negate: boolean): BodyMatch | null {
  switch (engine) {
    case 'mysql': {
      const re = new RegExp(`^JSON_CONTAINS_PATH\\(${FIELD_SRC}, 'one', ${pathLiteralSrc(engine)}\\) = ${negate ? '0' : '1'}$`);
      const m = body.match(re);
      if (!m) return null;
      const path = decodeTextLiteral(m[2], engine);
      return path === null ? null : { field: m[1], path };
    }
    case 'oracle': {
      const re = negate
        ? new RegExp(`^NOT JSON_EXISTS\\(${FIELD_SRC}, ${pathLiteralSrc(engine)}\\)$`)
        : new RegExp(`^JSON_EXISTS\\(${FIELD_SRC}, ${pathLiteralSrc(engine)}\\)$`);
      const m = body.match(re);
      if (!m) return null;
      const path = decodeTextLiteral(m[2], engine);
      return path === null ? null : { field: m[1], path };
    }
    case 'mssql': {
      // Root-path (`$`) only — the OPENJSON parent/key non-root shape is a
      // documented residual (see module doc).
      const re = negate
        ? new RegExp(`^\\(JSON_QUERY\\(${FIELD_SRC}, '\\$'\\) IS NULL AND JSON_VALUE\\(${FIELD_SRC}, '\\$'\\) IS NULL\\)$`)
        : new RegExp(`^\\(JSON_QUERY\\(${FIELD_SRC}, '\\$'\\) IS NOT NULL OR JSON_VALUE\\(${FIELD_SRC}, '\\$'\\) IS NOT NULL\\)$`);
      const m = body.match(re);
      if (!m || m[1] !== m[2]) return null;
      return { field: m[1], path: '$' };
    }
    case 'sqlite':
      return null;
  }
}

function matchHasProperty(engine: NonPgEngine, s: string, negate: boolean): GuardedMatch | null {
  const direct = matchHasPropertyBody(engine, s, negate);
  if (direct) return { ...direct, declaredOnText: false };
  const guard = matchGuardPrefix(engine, s);
  if (!guard) return null;
  const body = matchHasPropertyBody(engine, guard.rest, negate);
  if (!body || body.field !== guard.guardField) return null;
  return { ...body, declaredOnText: true };
}

/** `is_valid` / `is_not_valid` — declared-on-text form only (the native-column
 *  form is out of scope, see module doc). Standalone: no guard involved
 *  (these ARE the guard). */
function matchIsValid(engine: NonPgEngine, s: string, negate: boolean): { field: string } | null {
  let re: RegExp;
  switch (engine) {
    case 'mysql':
      re = new RegExp(`^JSON_VALID\\(${FIELD_SRC}\\) = ${negate ? '0' : '1'}$`);
      break;
    case 'mssql':
      re = new RegExp(`^ISJSON\\(${FIELD_SRC}\\) = ${negate ? '0' : '1'}$`);
      break;
    case 'sqlite':
      re = new RegExp(`^json_valid\\(${FIELD_SRC}\\) = ${negate ? '0' : '1'}$`);
      break;
    case 'oracle':
      re = negate ? new RegExp(`^${FIELD_SRC} IS NOT JSON$`) : new RegExp(`^${FIELD_SRC} IS JSON$`);
      break;
  }
  const m = s.match(re);
  return m ? { field: m[1] } : null;
}

function parseNonPgJsonPredicate(s: string, engine: NonPgEngine): JsonParseResult | null {
  for (const [sub, negate] of [['is_valid', false], ['is_not_valid', true]] as const) {
    const m = matchIsValid(engine, s, negate);
    if (m) {
      return {
        field: m.field,
        condition: { operator: 'json', jsonSubOperator: sub, jsonDeclaredOnText: true },
      };
    }
  }
  const guarded = (make: () => (body: string) => BodyMatch | null) => () => withOptionalGuard(engine, s, make());
  return firstJsonPathMatch([
    ['has_property', () => matchHasProperty(engine, s, false)],
    ['not_has_property', () => matchHasProperty(engine, s, true)],
    ['property_enum', guarded(() => mkPropertyEnumMatcher(engine))],
    ['path_eq', guarded(() => mkPathEqMatcher(engine))],
    ['path_neq', guarded(() => mkPathNeqMatcher(engine))],
    ['path_is_null', guarded(() => mkPathIsNullMatcher(engine, false))],
    ['path_is_not_null', guarded(() => mkPathIsNullMatcher(engine, true))],
    ['path_contains', guarded(() => mkPathContainsMatcher(engine))],
    ...JSON_NUMERIC_SUB_OPS.map(([sub, op]) => [sub, guarded(() => mkNumericOpMatcher(engine, op))] as const),
  ]);
}

/**
 * Parse a SQL predicate string that (may) be `buildJsonConditionSql`'s emit
 * for the given engine back into a structured `{ field, condition }`. Returns
 * `null` when the shape isn't confidently recognized (unsupported sub-op,
 * wildcard/recursive path, or not a JSON predicate at all) — the caller
 * falls back to raw text, never a best-guess chip.
 */
export function parseJsonPredicate(s: string, engine: DatabaseEngineId): JsonParseResult | null {
  switch (engine) {
    case 'pgsql':
      return parsePgJsonPredicate(s);
    case 'mssql':
    case 'mysql':
    case 'oracle':
    case 'sqlite':
      return parseNonPgJsonPredicate(s, engine);
    case 'mock':
    case 'kusto':
    case 'azurestorage':
      return null;
  }
}
